import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Count, Money } from '../components/common/Money'
import { DataTable, type Column } from '../components/common/DataTable'
import { EmptyState } from '../components/common/EmptyState'
import { DateCell } from '../components/entry/DateCell'
import { EntryGrid } from '../components/entry/EntryGrid'
import { NumericCell } from '../components/entry/NumericCell'
import { useBatches, useCreateBatch } from '../hooks/useBatches'
import { today } from '../hooks/useReports'
import { clientUuid } from '../lib/uuid'
import type { BatchRecord } from '@henlytics/types'

const columns: ReadonlyArray<Column<BatchRecord>> = [
  { key: 'code', header: 'Code', render: (b) => <Link to={`/batches/${b.id}`}>{b.code}</Link> },
  { key: 'breed', header: 'Breed', render: (b) => b.breed ?? '—' },
  { key: 'arrival', header: 'Arrived', render: (b) => b.arrivalDate },
  { key: 'chicks', header: 'Chicks', numeric: true, render: (b) => <Count value={b.chicksReceived} /> },
  { key: 'cost', header: 'Chick cost', numeric: true, render: (b) => <Money value={b.chickTotalCost} /> },
  { key: 'status', header: 'Status', render: (b) => b.status },
]

export function BatchList() {
  const { data, isPending, isError, error } = useBatches()
  const create = useCreateBatch()

  const [code, setCode] = useState('')
  const [breed, setBreed] = useState('')
  const [supplier, setSupplier] = useState('')
  const [arrivalDate, setArrivalDate] = useState(today())
  const [chicksReceived, setChicksReceived] = useState('')
  const [ageOnArrival, setAgeOnArrival] = useState('0')
  const [chickTotalCost, setChickTotalCost] = useState('')

  // §3.2 — one id per entry, held for the lifetime of the form so that retrying
  // a failed submit replays the same write instead of creating a second batch.
  const [ids, setIds] = useState(() => ({ batch: clientUuid(), arrival: clientUuid() }))

  const submit = () => {
    create.mutate(
      {
        code: code.trim(),
        breed: breed.trim() || null,
        supplier: supplier.trim() || null,
        arrivalDate,
        chicksReceived: Number(chicksReceived),
        chickAgeDaysOnArrival: Number(ageOnArrival || '0'),
        chickTotalCost,
        clientUuid: ids.batch,
        arrivalEventClientUuid: ids.arrival,
      },
      {
        onSuccess: () => {
          setCode('')
          setBreed('')
          setSupplier('')
          setChicksReceived('')
          setChickTotalCost('')
          // A new entry is a new fact and needs new ids.
          setIds({ batch: clientUuid(), arrival: clientUuid() })
        },
      },
    )
  }

  return (
    <div className="page">
      <header className="page-head">
        <h2>Batches</h2>
      </header>

      <EntryGrid
        title="New batch"
        description="Creating a batch also writes its arrival to the bird ledger. Arrival mortality is entered afterwards as a mortality dated the arrival date."
        onSubmit={submit}
        submitLabel="Create batch"
        busy={create.isPending}
        error={create.isError ? create.error.message : null}
        success={create.isSuccess ? 'Batch created.' : null}
      >
        <label className="cell">
          <span className="cell-label">Code</span>
          <input className="cell-input" value={code} onChange={(e) => setCode(e.target.value)} required />
        </label>
        <label className="cell">
          <span className="cell-label">Breed</span>
          <input className="cell-input" value={breed} onChange={(e) => setBreed(e.target.value)} />
        </label>
        <label className="cell">
          <span className="cell-label">Supplier</span>
          <input className="cell-input" value={supplier} onChange={(e) => setSupplier(e.target.value)} />
        </label>
        <DateCell label="Arrival date" value={arrivalDate} onChange={setArrivalDate} />
        <NumericCell label="Chicks received" value={chicksReceived} onChange={setChicksReceived} integer />
        <NumericCell
          label="Age on arrival (days)"
          value={ageOnArrival}
          onChange={setAgeOnArrival}
          integer
          hint="Brings layer start forward"
        />
        <NumericCell label="Chick cost (total)" value={chickTotalCost} onChange={setChickTotalCost} />
      </EntryGrid>

      {isPending ? <p className="muted">Loading…</p> : null}
      {isError ? <p role="alert" className="error">{error.message}</p> : null}
      {data ? (
        <DataTable
          rows={data}
          columns={columns}
          rowKey={(b) => b.id}
          empty={<EmptyState title="No batches yet." hint="Create the first one above." />}
        />
      ) : null}
    </div>
  )
}
